import { formatMoney } from '@theme/money-formatting';

class FreeShippingGoal extends HTMLElement {
  constructor() {
    super();
    this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    this.currency = this.dataset.currency || /** @type {any} */ (window).theme?.currency || 'USD';
    this.onCartUpdate = this.onCartUpdate.bind(this);
  }

  connectedCallback() {
    this.updateShippingGoal(Number(this.dataset.cartTotal));
    document.addEventListener('cart:update', this.onCartUpdate);
    document.addEventListener('cart:updated', this.onCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:update', this.onCartUpdate);
    document.removeEventListener('cart:updated', this.onCartUpdate);
  }

  /** @param {Event} event */
  onCartUpdate(event) {
    const cartEvent = /** @type {CustomEvent<any>} */ (event);
    const cart = cartEvent.detail?.resource ?? cartEvent.detail?.cart;
    const nextAmount = cart?.original_total_price ?? cart?.items_subtotal_price ?? cart?.total_price;
    if (typeof nextAmount === 'number') {
      this.dataset.cartTotal = String(nextAmount);
      this.updateShippingGoal(nextAmount);
      return;
    }

    const cartItemsComponent = /** @type {HTMLElement | null} */ (this.closest('cart-items-component'));
    const sectionId = cartItemsComponent?.dataset.sectionId;
    const sections = cartEvent.detail?.data?.sections;
    const sectionMarkup = sectionId && sections ? sections[sectionId] : null;
    if (sectionMarkup) {
      const newSectionHTML = new DOMParser().parseFromString(sectionMarkup, 'text/html');
      const nextGoalElement = newSectionHTML.querySelector('free-shipping-goal');
      const nextCartTotal = Number(nextGoalElement?.getAttribute('data-cart-total'));
      if (Number.isFinite(nextCartTotal)) {
        this.dataset.cartTotal = String(nextCartTotal);
        this.updateShippingGoal(nextCartTotal);
        return;
      }
    }

    requestAnimationFrame(() => {
      this.updateShippingGoal(Number(this.dataset.cartTotal || 0));
    });
  }

  /** @returns {{ amount: number, label: string, element: HTMLElement }[]} */
  #milestones() {
    return [...this.querySelectorAll('[data-reward-node]')]
      .map((element) => {
        if (!(element instanceof HTMLElement)) return null;
        return {
          amount: Number(element.dataset.amount) || 0,
          label: element.dataset.label || '',
          element,
        };
      })
      .filter((milestone) => milestone && milestone.amount > 0)
      .sort((a, b) => a.amount - b.amount);
  }

  /** @param {string} value */
  #escapeHtml(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;');
  }

  /**
   * @param {string} template
   * @param {{ amount?: string, reward?: string }} values
   */
  #fillTemplate(template, values) {
    return this.#escapeHtml(template)
      .replaceAll('[amount]', values.amount || '')
      .replaceAll('[reward]', this.#escapeHtml(values.reward || ''));
  }

  /**
   * @param {number} cartCents
   * @param {{ amount: number }[]} milestones
   */
  #progressPercent(cartCents, milestones) {
    const count = milestones.length;
    if (!count || cartCents <= 0) return 0;

    const rate = Number(window.Shopify?.currency?.rate || 1);
    let previousCents = 0;

    for (let index = 0; index < count; index += 1) {
      const nextCents = Math.round(milestones[index].amount * rate * 100);
      const start = (index / count) * 100;
      const end = ((index + 1) / count) * 100;
      if (cartCents < nextCents) {
        const span = nextCents - previousCents;
        const ratio = span > 0 ? (cartCents - previousCents) / span : 1;
        return Math.min(100, Math.max(0, start + ratio * (end - start)));
      }
      previousCents = nextCents;
    }

    return 100;
  }

  /** @param {number} amount */
  updateShippingGoal(amount) {
    const milestones = this.#milestones();
    const progressFill = this.querySelector('.progress-bar__fill');
    const progress = this.querySelector('.progress-bar');
    const status = this.querySelector('[data-reward-status]');
    if (!milestones.length || !(progressFill instanceof HTMLElement) || !(status instanceof HTMLElement)) return;

    const cartCents = Number.isFinite(amount) ? amount : 0;
    if (cartCents > 0) {
      this.classList.remove('hidden');
    } else {
      this.classList.add('hidden');
    }

    const rate = Number(window.Shopify?.currency?.rate || 1);
    const percent = this.#progressPercent(cartCents, milestones);
    progressFill.style.width = `${percent}%`;
    if (progress instanceof HTMLElement) {
      progress.setAttribute('aria-valuenow', String(Math.round(percent)));
      progress.dataset.value = String(cartCents / 100);
      progress.dataset.max = String(milestones[milestones.length - 1].amount * rate);
    }

    let reachedCount = 0;
    /** @type {{ amount: number, label: string } | null} */
    let nextMilestone = null;

    milestones.forEach((milestone, index) => {
      const thresholdCents = Math.round(milestone.amount * rate * 100);
      const reached = cartCents >= thresholdCents;
      milestone.element.classList.toggle('is-reached', reached);
      milestone.element.style.setProperty('--node-index', String(index));
      milestone.element.style.setProperty('--node-count', String(milestones.length));
      if (reached) {
        reachedCount += 1;
      } else if (!nextMilestone) {
        nextMilestone = milestone;
      }
    });

    const allReached = reachedCount === milestones.length;
    this.classList.toggle('free-shipping-goal--done', allReached);

    // Progress: only [amount] + copy. Done: Best reward unlocked (no mid-tier "X unlocked").
    if (allReached) {
      status.innerHTML = this.#escapeHtml(this.dataset.messageDone || 'Best reward unlocked');
    } else if (nextMilestone) {
      const remainingCents = Math.max(0, Math.round(nextMilestone.amount * rate * 100) - cartCents);
      const amountHtml = `<strong data-left-to-spend>${formatMoney(remainingCents, this.moneyFormat, this.currency)}</strong>`;
      const template = this.dataset.messageAway || 'Spend [amount] more to unlock [reward]';

      status.innerHTML = this.#fillTemplate(template, {
        amount: amountHtml,
        reward: nextMilestone.label,
      });
    }

    if (progress instanceof HTMLElement) {
      progress.setAttribute('aria-label', status.textContent?.trim() || '');
    }
  }
}

if (!customElements.get('free-shipping-goal')) {
  customElements.define('free-shipping-goal', FreeShippingGoal);
}
